import { useState, useEffect } from 'react';
import { 
  formatSessionDate,
  getWeekDate as getWeekDateUtil
} from '../utils/courseDataFetcher';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const COURSE_DATA_TIMEOUT_MS = 6500;
const FALLBACK_SESSION_DATE = {
  date: "Date TBD",
  time: "Time TBD",
  timezone: "",
  dayOfWeek: "Day TBD",
  rawDate: null
};

const useCourseData = (shouldFetch = true) => {
  const [bootcampData, setBootcampData] = useState(null);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);
  const [nextSessionDate, setNextSessionDate] = useState(null);
  const [sessionGroups, setSessionGroups] = useState({ past: [], current: null, future: [] });

  useEffect(() => {
    let isMounted = true;

    if (!shouldFetch) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchBootcampInfo = async () => {
      let timeoutId;
      setIsLoading(true);
      setError(null);
      
      try {
        const courseId = process.env.REACT_APP_AI_BOT_BUILDER_BOOTCAMP_COURSE_ID;
        if (!courseId) {
          console.warn('AI Bootcamp course ID not configured');
          if (isMounted) {
            setIsLoading(false);
          }
          return;
        }

        console.log('Fetching bootcamp data for course:', courseId);

        // Use the new optimized endpoint
        const apiUrl = getApiBaseUrl();
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), COURSE_DATA_TIMEOUT_MS);
        const response = await fetch(
          `${apiUrl}/courses/${courseId}/bootcamp-data`,
          { signal: controller.signal }
        );
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch bootcamp data: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Bootcamp data fetched:', data);

        if (!isMounted) return;
        
        // Set all the data from the optimized response
        setSessionGroups(data.sessionGroups);
        
        // Convert UTC time to user's local timezone for display
        // Also calculate dayOfWeek from the actual date (don't trust API value)
        if (data.nextSessionDate && data.nextSessionDate.rawDate) {
          const utcDate = new Date(data.nextSessionDate.rawDate);
          const localTimeString = utcDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const timezoneAbbr = utcDate.toLocaleTimeString('en-US', {
            timeZoneName: 'short'
          }).split(' ').pop();
          // Calculate dayOfWeek from actual date instead of using API value
          const dayOfWeek = utcDate.toLocaleDateString('en-US', { weekday: 'long' });

          setNextSessionDate({
            ...data.nextSessionDate,
            time: localTimeString,
            timezone: timezoneAbbr,
            dayOfWeek: dayOfWeek
          });
        } else {
          setNextSessionDate(data.nextSessionDate);
        }
        setBootcampData({
          course: data.course,
          sessionGroups: data.sessionGroups,
          nextSession: data.nextSession,
          nextSessionGroup: data.nextSession ? {
            id: data.nextSession.groupId,
            name: data.nextSession.groupName
          } : null
        });
        
      } catch (error) {
        if (!isMounted) return;
        console.error('Failed to fetch bootcamp data:', error);
        setError(error.name === 'AbortError' ? 'Timed out fetching bootcamp data' : error.message || 'Failed to fetch bootcamp data');
        setNextSessionDate(FALLBACK_SESSION_DATE);
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchBootcampInfo();

    return () => {
      isMounted = false;
    };
  }, [shouldFetch]);

  // Calculate weekly dates from the first session
  const getWeekDate = (weekNumber) => {
    if (nextSessionDate?.rawDate) {
      return getWeekDateUtil(new Date(nextSessionDate.rawDate), weekNumber);
    }
    return `Week ${weekNumber} TBD`;
  };

  return {
    bootcampData,
    isLoading,
    error,
    nextSessionDate: nextSessionDate || FALLBACK_SESSION_DATE,
    getWeekDate,
    formatSessionDate,
    sessionGroups
  };
};

export default useCourseData; 
